import { sendDataDogEvent } from '../datadog/events';
import { getJourneyId } from '../journeyId';
import type { Primitive } from '../types';

/**
 * Journey step events: one RUM action per step, `journey_step.<phase>.<step>`,
 * with a status and timing. The contract is in docs/journey-telemetry.md.
 *
 * State is in memory for the page load. A provider tab hosts many sessions:
 * `run` counts them, and a new run starts after `in_session.end`.
 */
export type JourneyPhase = 'arriving' | 'waiting' | 'joining' | 'in_session';

export type JourneyStep =
  // arriving (client): reach -> load -> identify -> prepare -> check_in
  // arriving (provider): load -> identify -> ready
  | 'arriving.reach'
  | 'arriving.load'
  | 'arriving.identify'
  | 'arriving.prepare'
  | 'arriving.check_in'
  | 'arriving.ready'
  // waiting: seen (provider) -> held (client) -> admitted (provider)
  | 'waiting.seen'
  | 'waiting.held'
  | 'waiting.admitted'
  // joining: deliver -> connect -> send -> hear
  | 'joining.deliver'
  | 'joining.connect'
  | 'joining.send'
  | 'joining.hear'
  // in session: stay connected, stay usable, tools work, end
  | 'in_session.stay_connected'
  | 'in_session.stay_usable'
  | 'in_session.tools_work'
  | 'in_session.end';

export type JourneyStepStatus = 'ok' | 'error' | 'skipped';
export type JourneyUserType = 'client' | 'provider';

/** Counted per run and reported on `in_session.end`. */
export type JourneySignal =
  | 'reconnecting'
  | 'reconnected'
  | 'unplanned_disconnect'
  | 'rejoining'
  | 'recovered'
  | 'connectivity_issue';

export const JOURNEY_STEP_ACTION_PREFIX = 'journey_step.';
export const JOURNEY_PAGE_EXIT_ACTION = 'journey_page_exit';

const PHASE_ORDER: JourneyPhase[] = ['arriving', 'waiting', 'joining', 'in_session'];
/** Steps that start a new run after `in_session.end`. */
const RUN_START_STEPS = new Set<JourneyStep>([
  'waiting.seen',
  'waiting.held',
  'waiting.admitted',
  'joining.deliver',
  'joining.connect',
]);
/** `session_total_ms` runs from the first of these. */
const ADMISSION_STEPS = new Set<JourneyStep>(['waiting.admitted', 'joining.deliver', 'joining.connect']);
/** Can happen in any phase (chat in the waiting room, a disconnect while joining); never move it. */
const NON_ADVANCING_STEPS = new Set<JourneyStep>([
  'in_session.tools_work',
  'in_session.stay_connected',
  'in_session.stay_usable',
]);
/** Step that closes a phase, mapped to the phase it opens (null: the run is over). */
const PHASE_COMPLETING_STEPS: Partial<Record<JourneyStep, JourneyPhase | null>> = {
  'arriving.check_in': 'waiting',
  'arriving.ready': 'waiting',
  'waiting.admitted': 'joining',
  'joining.hear': 'in_session',
  'in_session.end': null,
};
/** Reported as an error at the first signal of a run, otherwise ok at the end. */
const QUALITY_STEP_FOR_SIGNAL: Partial<Record<JourneySignal, JourneyStep>> = {
  unplanned_disconnect: 'in_session.stay_connected',
  connectivity_issue: 'in_session.stay_usable',
};

type JourneyRecord = {
  startedAt: number;
  lastStepAt: number;
  lastStep?: JourneyStep;
  phase?: JourneyPhase;
  phaseStartedAt?: number;
  /** ok/skipped steps, recorded once per run */
  recorded: JourneyStep[];
  /** quality steps already reported as errors */
  failed: JourneyStep[];
  signals: Record<JourneySignal, number>;
  ended: boolean;
  /** 1-based session count in this tab */
  run: number;
  /** first admission step; anchors `session_total_ms` */
  admittedAt?: number;
  /** last `waiting.seen`; anchors the next run's waiting clock */
  lastSeenAt?: number;
};

export type RecordJourneyStepOptions = {
  status?: JourneyStepStatus;
  /** ok/skipped steps emit once per run unless set; errors always emit. */
  repeatable?: boolean;
} & Record<string, Primitive>;

let userType: JourneyUserType | undefined;
let current: JourneyRecord | undefined;
let extraContext: Record<string, Primitive> = {};

function emptySignals(): Record<JourneySignal, number> {
  return {
    reconnecting: 0,
    reconnected: 0,
    unplanned_disconnect: 0,
    rejoining: 0,
    recovered: 0,
    connectivity_issue: 0,
  };
}

function emptyRecord(now: number, run = 1): JourneyRecord {
  return {
    startedAt: now,
    lastStepAt: now,
    recorded: [],
    failed: [],
    signals: emptySignals(),
    ended: false,
    run,
  };
}

function readRecord(now: number): JourneyRecord {
  current ??= emptyRecord(now);
  return current;
}

function writeRecord(record: JourneyRecord) {
  current = record;
}

function phaseOf(step: JourneyStep): JourneyPhase {
  return step.split('.')[0] as JourneyPhase;
}

/** The user reached the room, so there is a session to end. */
function hasSession(record: JourneyRecord) {
  return record.recorded.includes('joining.connect') || record.phase === 'in_session';
}

function baseContext(record: JourneyRecord): Record<string, Primitive> {
  return {
    journey_id: getJourneyId(),
    user_type: userType,
    ...extraContext,
    run: record.run,
  };
}

export function setJourneyUserType(type: JourneyUserType) {
  userType = type;
}

export function getJourneyUserType() {
  return userType;
}

/** Merged into every subsequent event; undefined values are dropped. */
export function setJourneyContext(context: Record<string, Primitive>) {
  extraContext = Object.fromEntries(
    Object.entries({ ...extraContext, ...context }).filter(([, value]) => value !== undefined),
  );
}

/** Returns true when an event was sent. */
export function recordJourneyStep(step: JourneyStep, options: RecordJourneyStepOptions = {}): boolean {
  const { status = 'ok', repeatable = false, ...extra } = options;
  const now = Date.now();
  let record = readRecord(now);
  const stepPhase = phaseOf(step);
  const advances = !NON_ADVANCING_STEPS.has(step);

  // Providers do not always complete before admitting the next client
  if (step === 'waiting.admitted' && !record.ended && hasSession(record)) {
    recordSessionEnd({ reason: 'superseded' });
    record = readRecord(now);
  }

  if (record.ended) {
    // A visitor seen during the open session was already waiting: start the
    // new run at the seen time
    const seenAt =
      record.lastSeenAt !== undefined &&
      record.lastSeenAt > (record.admittedAt ?? 0) &&
      (step === 'waiting.admitted' || step === 'in_session.tools_work')
        ? record.lastSeenAt
        : undefined;
    if (RUN_START_STEPS.has(step) || seenAt !== undefined) {
      record = {
        ...emptyRecord(seenAt ?? now, record.run + 1),
        ...(seenAt !== undefined ? { phase: 'waiting', phaseStartedAt: seenAt } : {}),
        recorded: record.recorded.filter((recorded) => phaseOf(recorded) === 'arriving'),
      };
    } else if (stepPhase === 'in_session') {
      // Trailing work from the ended session (transcription flush, teardown)
      return false;
    }
  }

  const phase = advances ? stepPhase : (record.phase ?? stepPhase);

  // A connect after an unplanned disconnect is a recovery even when the
  // connect step itself is deduped
  const signals =
    step === 'joining.connect' && status === 'ok' && record.signals.unplanned_disconnect > record.signals.recovered
      ? { ...record.signals, recovered: record.signals.recovered + 1 }
      : record.signals;

  const once = (status === 'ok' || status === 'skipped') && !repeatable;
  if (once && record.recorded.includes(step)) {
    if (signals !== record.signals) writeRecord({ ...record, signals });
    return false;
  }

  const phaseChanged = advances && record.phase !== undefined && record.phase !== phase;
  const movingForward = advances && (!record.phase || PHASE_ORDER.indexOf(phase) >= PHASE_ORDER.indexOf(record.phase));
  const regressesToWaiting = step === 'joining.connect' && status === 'error';
  // Idle time before a visitor shows up is not waiting
  const restartsWaiting = step === 'waiting.seen' && record.phase === 'waiting';
  const completesPhase =
    status === 'ok' && step in PHASE_COMPLETING_STEPS && (record.phase === undefined || record.phase === stepPhase);
  const nextPhase = PHASE_COMPLETING_STEPS[step];

  const context: Record<string, Primitive> = {
    ...baseContext(record),
    phase,
    step,
    status,
    elapsed_ms: now - record.startedAt,
    since_previous_ms: now - record.lastStepAt,
    previous_step: record.lastStep,
    ...(phaseChanged && movingForward && record.phase && record.phaseStartedAt !== undefined
      ? { previous_phase: record.phase, previous_phase_duration_ms: now - record.phaseStartedAt }
      : {}),
    ...(completesPhase
      ? {
          completed_phase: stepPhase,
          completed_phase_duration_ms: now - (record.phaseStartedAt ?? record.startedAt),
        }
      : {}),
    ...extra,
  };

  sendDataDogEvent(`${JOURNEY_STEP_ACTION_PREFIX}${step}`, context);

  let phaseUpdate: Pick<JourneyRecord, 'phase' | 'phaseStartedAt'> | Record<string, never> = {};
  if (regressesToWaiting || restartsWaiting) phaseUpdate = { phase: 'waiting', phaseStartedAt: now };
  else if (completesPhase && nextPhase) phaseUpdate = { phase: nextPhase, phaseStartedAt: now };
  else if (movingForward && (phaseChanged || !record.phase)) phaseUpdate = { phase, phaseStartedAt: now };

  writeRecord({
    ...record,
    signals,
    lastStepAt: now,
    lastStep: step,
    ...phaseUpdate,
    ...(record.admittedAt === undefined && ADMISSION_STEPS.has(step) ? { admittedAt: now } : {}),
    ...(step === 'waiting.seen' ? { lastSeenAt: now } : {}),
    recorded: once ? [...record.recorded, step] : record.recorded,
    failed: status === 'error' && !record.failed.includes(step) ? [...record.failed, step] : record.failed,
  });
  return true;
}

/**
 * Counts a signal. The first disconnect or connectivity issue of a run also
 * reports its quality step as an error right away, so a closed tab still
 * shows the failure.
 */
export function noteJourneySignal(signal: JourneySignal) {
  const record = readRecord(Date.now());
  // teardown noise after the end
  if (record.ended) return;
  writeRecord({ ...record, signals: { ...record.signals, [signal]: record.signals[signal] + 1 } });

  const qualityStep = QUALITY_STEP_FOR_SIGNAL[signal];
  if (qualityStep && !record.failed.includes(qualityStep)) {
    recordJourneyStep(qualityStep, { status: 'error', reason: signal });
  }
}

/**
 * Ends the run: emits the quality steps that did not fail, then
 * `in_session.end`. Returns false when there is nothing to end.
 */
export function recordSessionEnd({ reason }: { reason: string }) {
  const now = Date.now();
  const record = readRecord(now);
  if (record.ended || !hasSession(record)) return false;

  const { signals } = record;
  const counters = {
    reconnecting_count: signals.reconnecting,
    unplanned_disconnect_count: signals.unplanned_disconnect,
    recovered_count: signals.recovered,
    connectivity_issue_count: signals.connectivity_issue,
  };
  if (!record.failed.includes('in_session.stay_connected')) recordJourneyStep('in_session.stay_connected');
  if (!record.failed.includes('in_session.stay_usable')) recordJourneyStep('in_session.stay_usable');
  const sent = recordJourneyStep('in_session.end', {
    reason,
    ...counters,
    // run start: page load for run 1
    journey_total_ms: now - record.startedAt,
    // first admission step: comparable across runs and user types
    session_total_ms: now - (record.admittedAt ?? record.startedAt),
  });
  writeRecord({ ...readRecord(now), ended: true });
  return sent;
}

/**
 * Marker sent on pagehide with where the journey was. Not an end: a run with
 * a page exit and no `in_session.end` is an abandoned tab.
 */
export function recordJourneyPageExit() {
  if (!current) return false;
  const now = Date.now();
  sendDataDogEvent(JOURNEY_PAGE_EXIT_ACTION, {
    ...baseContext(current),
    phase: current.phase,
    last_step: current.lastStep,
    ended: current.ended,
    elapsed_ms: now - current.startedAt,
    since_previous_ms: now - current.lastStepAt,
    ...(current.phaseStartedAt !== undefined ? { phase_elapsed_ms: now - current.phaseStartedAt } : {}),
    ...(current.admittedAt !== undefined ? { session_elapsed_ms: now - current.admittedAt } : {}),
  });
  return true;
}

export function resetJourneyStepsForTests() {
  userType = undefined;
  current = undefined;
  extraContext = {};
}
